/// <amd-module name="VSS/Legacy/LegacyPlatformService" />
import React = require("react");

import { ILegacyPlatformService } from "VSS/Legacy/Types";
import { IContentAttributes, IVssContentService } from "VSS/Platform/Content";
import {
    FastPageSwitchEvent,
    FastPageSwitchEventCompletedDetail,
    IVssPageContext,
    IVssService,
    ServiceOptions,
    Services,
    VssService
} from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssComponentContext } from "VSS/Platform/Layout";
import { IVssNavigationService } from "VSS/Platform/Navigation";
import { IVssPerformanceService } from "VSS/Platform/Performance";

interface ILegacyContent extends IContentAttributes {
    bundle: Object;
}

interface ILegacyPlatformShim {
    content: ILegacyContent[];
    scriptsToRequire: string[];

    builtInPlugins: Object;
    builtInPluginBases: Object;
    contributionsPageData: Object;
    pageContext: any;
    featureLicenseInfo: Object;

    preloadContent: boolean;
}

const legacyPlatformDataProvider = "ms.vss-web.legacy-platform-data-provider";
const legacyPlatformVssUIContent = "ms.vss-web.legacy-vss-ui-content";

const legacyPlatformContributions = [legacyPlatformVssUIContent, legacyPlatformDataProvider];

const global = window as any;

let legacyPlatformPageContext: IVssPageContext;

class LegacyPlatformService extends VssService implements ILegacyPlatformService {
    private _legacyContentLoadedCallbacks?: { (): void }[];
    private _legacyContentLoaded: boolean;
    private _legacyDataElement: HTMLDivElement;
    private _legacyDataReset: boolean;

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        legacyPlatformPageContext = this.pageContext;

        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const legacyData = contributionService.getData<ILegacyPlatformShim>(legacyPlatformDataProvider);
        if (legacyData && legacyData.preloadContent) {
            this._ensureLegacyContentLoaded(() => {});
        }
    }

    public loadModules(moduleNames?: string[], dependentContributionIds?: string[]): Promise<void> {
        return new Promise((resolve, reject) => {
            const resolveModules = () => {
                if (moduleNames && moduleNames.length) {
                    global
                        .requirejs("VSS/VSS")
                        .requireModules(moduleNames)
                        .then(() => {
                            resolve();
                        });
                } else {
                    resolve();
                }
            };

            this._ensureLegacyContentLoaded(() => {
                if (dependentContributionIds && dependentContributionIds.length) {
                    // Fetch the contributions before we require any (possibly-remote) modules
                    global.requirejs(["VSS/Service", "VSS/Contributions/Services"], (_Service: any, _ContributionsService: any) => {
                        _Service
                            .getService(_ContributionsService.ExtensionService)
                            .getContributions(dependentContributionIds, true, true, true)
                            .then((contributions: { type: string }[]) => {
                                // The legacy contribution service does not load new-platform content modules. If any content
                                // contributions are returned, ensure we process this contribution request in the new platform as well.
                                if (contributions.some(c => c.type === "ms.vss-web.content")) {
                                    const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
                                    contributionService.getContributionsAsync(dependentContributionIds).then(resolveModules);
                                } else {
                                    resolveModules();
                                }
                            });
                    });
                } else {
                    resolveModules();
                }
            });
        });
    }

    public getLegacyPageContext(ensureTeam?: boolean): Promise<object> {
        return this.loadModules().then(() => {
            let pageContext = global.__vssPageContext;
            if (ensureTeam && !pageContext.webContext.team) {
                const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
                return contributionService
                    .getDataAsync<{ id: string; name: string }>("ms.vss-tfs-web.team-context-data-provider")
                    .then(teamContextData => {
                        if (teamContextData) {
                            // Clone relevant part of the page context and include team information for extensions
                            pageContext = {
                                ...pageContext,
                                webContext: {
                                    ...pageContext.webContext,
                                    team: {
                                        ...teamContextData
                                    }
                                }
                            };
                        }
                        return pageContext;
                    });
            } else {
                return global.__vssPageContext;
            }
        });
    }

    public requireModules(moduleNames: string[]): Promise<any[]> {
        return this.loadModules(moduleNames).then(() => {
            return moduleNames.map(m => global.requirejs(m));
        });
    }

    private _ensureLegacyContentLoaded(callback: () => void): void {
        if (this._legacyContentLoaded || global.__legacyContentLoaded) {
            callback();
        } else if (this._legacyContentLoadedCallbacks) {
            this._legacyContentLoadedCallbacks.push(callback);
        } else {
            this._legacyContentLoadedCallbacks = [callback];

            const performanceService = this.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
            performanceService.addSplitTiming("LegacyPlatformService.LoadStarted");

            const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
            const contentService = this.pageContext.getService<IVssContentService>("IVssContentService");
            const legacyData = contributionService.getData<ILegacyPlatformShim>(legacyPlatformDataProvider);
            if (legacyData && contentService.isLoaded(legacyPlatformVssUIContent)) {
                this._processLegacyData(legacyData);
            } else {
                contributionService.getContributionsAsync(legacyPlatformContributions).then(() => {
                    const legacyData = contributionService.getData<ILegacyPlatformShim>(legacyPlatformDataProvider);
                    if (legacyData) {
                        // The returned page context won't know about the current page's project information.
                        // We can fill it in now with the TFS navigation data provider. Note that this is
                        // cheating, this layer shouldn't know about a TFS-specific data provider. But this
                        // is not something we need to add extensibility/infrastructure for -- the legacy
                        // platform will not be changing.
                        const tfsNavData = contributionService.getData<any>("ms.vss-tfs-web.page-data");
                        if (tfsNavData) {
                            if (tfsNavData.project) {
                                legacyData.pageContext.webContext.project = {
                                    id: tfsNavData.project.id,
                                    name: tfsNavData.project.name
                                };
                            }
                            if (tfsNavData.team) {
                                legacyData.pageContext.webContext.team = {
                                    id: tfsNavData.team.id,
                                    name: tfsNavData.team.name
                                };
                            }
                        }
                        this._processLegacyData(legacyData);
                    }
                });
            }
        }
    }

    private _processLegacyData(legacyData: ILegacyPlatformShim): void {
        // Inject content and scripts
        const contentService = this.pageContext.getService<IVssContentService>("IVssContentService");
        contentService.loadScripts(legacyData.content).then(() => {
            const performanceService = this.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
            performanceService.addSplitTiming("LegacyPlatformService.ContentLoaded");

            this._processLegacyDataWithDOM(legacyData);
        });
    }

    private _processLegacyDataWithDOM(legacyData: ILegacyPlatformShim) {
        // Create a root element under the body to host legacy data
        this._legacyDataElement = document.createElement("div");
        this._legacyDataElement.classList.add("legacy-data");
        this._legacyDataElement.style.display = "none";
        document.body.appendChild(this._legacyDataElement);

        // Create JSON islands for data expected by the legacy platform
        this._createLegacyJsonIsland(this._legacyDataElement, "feature-licenses", legacyData.featureLicenseInfo);
        this._createLegacyJsonIsland(this._legacyDataElement, "vss-contribution-data", legacyData.contributionsPageData);
        this._createLegacyJsonIsland(this._legacyDataElement, "vss-bundle-data", legacyData.content.filter(c => !!c.bundle));

        // Set global variables expected by the legacy platform
        global._builtinPlugins = legacyData.builtInPlugins;
        global._builtInBases = legacyData.builtInPluginBases;

        if (global.__vssPageContext) {
            global.__vssPageContext.webContext.project = legacyData.pageContext.webContext.project;
            global.__vssPageContext.webContext.team = legacyData.pageContext.webContext.team;
            global.__vssPageContext.diagnostics.activityId = legacyData.pageContext.diagnostics.activityId;
        } else {
            global.__vssPageContext = legacyData.pageContext;
        }

        global.__cultureInfo = global.__vssPageContext.microsoftAjaxConfig.cultureInfo;

        // RequireJS config
        global.requirejs.config(global.__vssPageContext.moduleLoaderConfig);

        // Making React work with uppercase R
        global.requirejs.config({ map: { "*": { React: "react" } } });

        // Set current route information
        const navigationService = this.pageContext.getService<IVssNavigationService>("IVssNavigationService");
        const currentRoute = navigationService.getCurrentRoute();
        global.__vssPageContext.navigation.routeId = currentRoute.routeId;
        global.__vssPageContext.navigation.routeTemplates = currentRoute.routeTemplates;

        // Save the page context for use by getLWPService
        legacyPlatformPageContext = this.pageContext;

        // An async call to resolve the legacy data provider may not have the
        // selected hub information set (it only carries route information)
        // So set the appropriate information here based on the navigation service's
        // set of selected nav elements.
        const hubsContext = legacyData.pageContext.hubsContext;
        if (!hubsContext.selectedHubId) {
            const navElements = navigationService.getDisplayedNavigation();
            if (navElements.length > 3) {
                const hubGroupId = navElements[2];
                const hubId = navElements[3];
                hubsContext.selectedHubGroupId = hubGroupId;
                hubsContext.selectedHubId = hubId;
                if (!hubsContext.hubs.some((h: any) => h.id === hubId)) {
                    const hub = navigationService.getNavigation(hubId);
                    if (hub) {
                        hubsContext.hubs.push({
                            groupId: hubGroupId,
                            id: hubId,
                            name: hub.name,
                            supportsXHRNavigate: true,
                            uri: hub.href
                        });
                    }
                }
            }
        }

        document.body.addEventListener("fpsLoading", () => {
            global.__legacyFpsLoading = true;
        });

        if (this._legacyDataReset) {
            global.__updatedLegacyData = legacyData;
            this._legacyDataReset = false;
        }

        // At this point we'll be loading new platform content, so subscribe to FPS events now
        // to record updated legacy platform data.
        document.body.addEventListener("fpsCompleted", this._onFpsCompleted);

        // Issue require statement for initial scripts
        window.performance && window.performance.mark && window.performance.mark("requireStart");
        global.requirejs(legacyData.scriptsToRequire, () => {
            window.performance && window.performance.mark && window.performance.mark("requireEnd");
            global.requiredModulesLoaded = true;

            // Fire a legacy platform event indicating that initial scripts have been loaded
            global.requirejs(["VSS/Events/Page"], (pageEvents: any) => {
                pageEvents.getService().fire(pageEvents.CommonPageEvents.InitialScriptsLoaded);
            });

            // Best-effort attempt to kick-start/initialize the legacy platform's data provider
            // service. This allows the legacy platform to process any ServiceContext data
            // providers that were included directly on the page.
            try {
                const ServiceModule = global.requirejs("VSS/Service");
                const ContributionServiceModule = global.requirejs("VSS/Contributions/Services");
                const webPageDataService = ServiceModule.getService(ContributionServiceModule.WebPageDataService);
                webPageDataService.ensureDataProvidersResolved([]);
            } catch (ex) {
                console.warn(`Failed to invoke ensureDataProvidersResolved: ${ex}`);
            }

            // Now indicate that we've loaded and invoke pending callbacks
            this._legacyContentLoaded = true;

            if (this._legacyContentLoadedCallbacks) {
                for (const callback of this._legacyContentLoadedCallbacks) {
                    callback();
                }
                delete this._legacyContentLoadedCallbacks;
            }
        });
    }

    private _onFpsCompleted = (completedEvent: FastPageSwitchEvent<FastPageSwitchEventCompletedDetail>) => {
        if (completedEvent.detail.success) {
            const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
            const legacyData = contributionService.getData<ILegacyPlatformShim>(legacyPlatformDataProvider);

            global.__legacyFpsLoading = false;
            global.__updatedLegacyData = legacyData;
            legacyPlatformPageContext = this.pageContext;

            if (legacyData) {
                // Update pageContext properties that may have changed in the new FPS target.
                const legacyPageContext = global.__vssPageContext;
                legacyPageContext.webContext.project = legacyData.pageContext.webContext.project;
                legacyPageContext.webContext.team = legacyData.pageContext.webContext.team;
                legacyPageContext.diagnostics.activityId = legacyData.pageContext.diagnostics.activityId;
            } else {
                // New page does not carry a legacy context. Reset what we've got.
                this._legacyContentLoaded = false;
                this._legacyDataReset = true;
                document.body.removeEventListener("fpsLoading", this._onFpsCompleted);
                this._legacyDataElement.remove();
            }

            document.body.dispatchEvent(new CustomEvent("legacyFpsComplete", { detail: legacyData }));
        }
    };

    private _createLegacyJsonIsland(container: HTMLElement, className: string, data: any): void {
        const jsonIslandElem = document.createElement("script");
        jsonIslandElem.type = "application/json";
        jsonIslandElem.classList.add(className);
        jsonIslandElem.innerText = JSON.stringify(data);
        container.appendChild(jsonIslandElem);
    }
}

// Register the legacy platform service as persistant since legacy content persists across navigation boundaries
// without the concept of a service factory (IVssPageContext) which resets on navigation
Services.add("ILegacyPlatformService", { serviceFactory: LegacyPlatformService, options: ServiceOptions.Persistant | ServiceOptions.Startup });

/**
 * Method exposed to legacy web platform content to allow getting a new web platform service
 *
 * @param serviceName Name of the registered service
 */
export function getLWPService<T extends IVssService>(serviceName: string): T | undefined {
    if (legacyPlatformPageContext) {
        return legacyPlatformPageContext.getService<T>(serviceName);
    }
    return undefined;
}

/**
 * Method exposed to legacy web platform content to get props for an LWPComponent
 */
export function getLWPComponentProps(): IVssComponentContext {
    return { pageContext: legacyPlatformPageContext };
}
